import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import {
  ListPickupDetail,
  ListStt,
  PickupCorporateAddress
} from "@/domain/entities/ListPickupDetailV2";
import {
  ListPickupV2,
  PickupCorporateSummary
} from "@/domain/entities/ListPickupV2";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { AxiosResponse } from "axios";

export class ListPickupV2Mapper {
  public convertPickupCorporateSummaryDataFromApi(
    res: AxiosResponse<any>
  ): PickupCorporateSummary {
    const {
      data: { data }
    } = res;

    const card = {
      waitingCourier: data.waiting_courier,
      pickUpPackage: data.pick_up_package,
      pickUpDone: data.pick_up_done,
      agentHub: data.agent_hub
    };

    if (data.summary_state === "suggestion") {
      return new PickupCorporateSummary({
        state: data.summary_state,
        koliCount: data.suggestion?.estimate_total_koli ?? 0,
        weightKg: data.suggestion?.estimate_total_tonase ?? 0,
        contactName: data.default_address?.pca_name ?? "",
        contactPhone: data.default_address?.pca_phone_number ?? "",
        addressLine: data.default_address?.pca_address ?? "",
        nearestScheduleLabel:
          data.nearest_scheduled_pickup?.pickup_schedule ?? "",
        specialHandling: data.suggestion?.special_handling ?? [],
        productTypes: data.product_types ?? [],
        defaultAddressId: data.default_address?.pca_id ?? 0,
        showActivePickupCta: data.has_active_pickup,
        activePickupCount: data.active_pickup_count,
        ...card
      });
    } else if (data.summary_state === "bucket") {
      return new PickupCorporateSummary({
        state: data.summary_state,
        sttCount: data.bkd_count,
        koliCount: data.total_koli,
        weightKg: data.total_tonase,
        contactName: data.default_address?.pca_name ?? "",
        contactPhone: data.default_address?.pca_phone_number ?? "",
        addressLine: data.default_address?.pca_address ?? "",
        nearestScheduleLabel:
          data.nearest_scheduled_pickup?.pickup_schedule ?? "",
        specialHandling: data.special_handling ?? [],
        productTypes: data.product_types ?? [],
        defaultAddressId: data.default_address?.pca_id ?? 0,
        showActivePickupCta: data.has_active_pickup,
        activePickupCount: data.active_pickup_count,
        ...card
      });
    } else {
      return new PickupCorporateSummary({
        state: data.summary_state,
        contactName: data.default_address?.pca_name ?? "",
        contactPhone: data.default_address?.pca_phone_number ?? "",
        addressLine: data.default_address?.pca_address ?? "",
        defaultAddressId: data.default_address?.pca_id ?? 0,
        ...card
      });
    }
  }

  public convertDetailDataFromApi(res: AxiosResponse<any>): ListPickupDetail {
    const {
      data: { data }
    } = res;

    const listStt = Array.isArray(data.list_stt)
      ? data.list_stt.map(
          (item: any) =>
            new ListStt({
              bookingDate: item.booking_date,
              grossWeight: item.gross_weight,
              serviceCode: item.service_code,
              sttNo: item.stt_no,
              totalKoli: item.total_koli,
              dhlLabelPrintUrl: item.dhl_label_print_url ?? ""
            })
        )
      : [];

    const mergedFromShipments = Array.isArray(data.merged_from_shipments)
      ? data.merged_from_shipments.map((item: any) => ({
          shipmentId: item.shipment_id,
          totalKoli: item.total_koli ?? 0,
          totalWeight: item.total_weight ?? 0
        }))
      : [];

    return new ListPickupDetail({
      the3LcPickup: data["3lc_pickup"],
      actualTotalKoli: data.actual_total_koli,
      actualTotalShipment: data.actual_total_shipment,
      actualTotalTonase: data.actual_total_tonase,
      additionalKoli: data.additional_koli ?? 0,
      clientId: data.client_id,
      clientParentId: data.client_parent_id,
      clientRef: data.client_ref,
      courierId: data.courier_id ?? 0,
      courierName: data.courier_name,
      courierNote: data.courier_note,
      courierPhone: data.courier_phone,
      cancelNote: data.cancel_note,
      createdAt: data.created_at,
      createdBy: data.created_by,
      dropOffProof: Array.isArray(data.drop_off_proof)
        ? data.drop_off_proof
        : [],
      estimateTotalKoli: data.estimate_total_koli,
      estimateTotalShipment: data.estimate_total_shipment,
      dhlLabelPrintUrl: data.dhl_label_print_url ?? "",
      estimateTotalTonase: data.estimate_total_tonase,
      geoloc: data.geoloc,
      isDhlFirstMilePrintEnabled: data.is_dhl_first_mile_print_enabled ?? false,
      latestStatus: data.latest_status,
      listStt: listStt,
      mergedFromShipmentIds: Array.isArray(data.merged_from_shipment_ids)
        ? data.merged_from_shipment_ids
        : [],
      mergedFromShipments,
      mergedToShipmentId: data.merged_to_shipment_id ?? "",
      origin: data.origin,
      pickupAddress: data.pickup_address,
      pickupDate: data.pickup_date,
      pickupProof: Array.isArray(data.pickup_proof) ? data.pickup_proof : [],
      pickupTime: data.pickup_time,
      pickupType: data.pickup_type,
      actualPickupDateTime: data.actual_pickup_date_time,
      plateNo: data.plate_no,
      serviceCode: data.service_code,
      shipmentId: data.shipment_id,
      specialHandling: Array.isArray(data.special_handling)
        ? data.special_handling
        : [],
      agentHubTotalShipment: data.agent_hub_total_shipment ?? 0,
      agentHubTotalTonase: data.agent_hub_total_tonase ?? 0,
      agentHubTotalKoli: data.agent_hub_total_koli ?? 0,
      transportType: data.transport_type,
      updatedAt: data.updated_at,
      updatedBy: data.updated_by,
      timezone: data.timezone,
      pickupCorporateAddress: new PickupCorporateAddress({
        pcaId: data.pickup_corporate_address?.pca_id ?? 0,
        pcaName: data.pickup_corporate_address?.pca_name ?? "",
        pcaLatlon: data.pickup_corporate_address?.pca_latlon ?? "",
        pcaIsDefault: data.pickup_corporate_address?.pca_is_default ?? false,
        pcaPhoneNumber: data.pickup_corporate_address?.pca_phone_number ?? "",
        pcaAddress: data.pickup_corporate_address?.pca_address ?? "",
        pcaPicName: data.pickup_corporate_address?.pca_pic_name ?? "",
        pcaPostalCode: data.pickup_corporate_address?.pca_postal_code ?? "",
        pcaDistrictId: data.pickup_corporate_address?.pca_district_id ?? 0,
        pcaDistrictName: data.pickup_corporate_address?.pca_district?.name ?? ""
      })
    });
  }

  public convertListDataFromApi(res: AxiosResponse): ResponsePayloadV2 {
    const { data } = res;

    return new ResponsePayloadV2({
      pagination: new PaginationV2({
        page: data.meta.page,
        limit: data.meta.per_page,
        totalData: data.meta.total_records
      }),
      data: data.data?.length
        ? data.data.map(
            (item: any) => new ListPickupV2(convertSnakeToCamel(item))
          )
        : []
    });
  }
}
